import { Component, OnInit, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { CmsserviceService } from '../../common-cms-service/cmsservice.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { takeUntil, debounceTime, catchError, take } from 'rxjs/operators';
import { Subject, forkJoin, of, Observable } from 'rxjs';

// ── Types ────────────────────────────────────────────────────
export interface TtIconItem { icon: string; title: string; text: string; }
export interface TtStat { num: string; suffix?: string; label: string; }
export interface TtStep { title: string; text: string; }
export interface TtTestimonial { initial: string; name: string; place: string; text: string; }
export interface TtFaq { q: string; a: string; }
export interface TtMonth { value: number; label: string; }

// ── Static content ───────────────────────────────────────────
const TT_GUIDE: TtIconItem[] = [
  { icon: 'ti-books', title: 'Complete 11+ Topic Covrage', text: 'Maths, English, Creative Writing, VR and NVR' },
  { icon: 'ti-report-analytics', title: 'Detailed Result Analysis', text: 'After every single test' },
  { icon: 'ti-video', title: 'Expert Led Doubt Clearing Sessions', text: 'After every single test' },
  { icon: 'ti-certificate', title: 'Exam Aligned Practice', text: 'GL, CSSE, FSCE and CEM' },
  { icon: 'ti-map-pin', title: '7 centres or online', text: 'Face to face across England' }
];

const TT_STATS: TtStat[] = [
  { num: '15', label: 'Week programme' },
  { num: '5', suffix: '+', label: 'UK exam boards' },
  { num: '7', label: 'Test centres' },
  { num: '100', suffix: '%', label: 'Syllabus coverage' }
];

const TT_SUBJECTS: TtIconItem[] = [
  { icon: 'ti-math-symbols', title: 'Maths', text: 'Problem-solving and reasoning across numbers, shapes, data and sequences.' },
  { icon: 'ti-book-2', title: 'English', text: 'Reading comprehension, grammar, vocabulary, cloze and literary analysis.' },
  { icon: 'ti-pencil', title: 'Creative Writing', text: 'Imagination and expression through structured storytelling and composition.' },
  { icon: 'ti-puzzle', title: 'VR and NVR', text: 'Verbal and non-verbal reasoning: logic, patterns, sequences and spatial thinking.' }
];

const TT_FEATURES: TtIconItem[] = [
  { icon: 'ti-target', title: 'Targeted practice', text: 'Each week focuses on a set of topics, so practice is never scattered.' },
  { icon: 'ti-chart-bar', title: 'Regular progress tracking', text: 'A report after every test shows how scores move week on week.' },
  { icon: 'ti-rocket', title: 'Confidence, week by week', text: 'A steady routine that gets students ready for the full mock series.' }
];

const TT_BENEFITS: TtIconItem[] = [
  { icon: 'ti-zoom-check', title: 'Identify weak areas', text: 'Focused questions show where improvement is needed most.' },
  { icon: 'ti-report-analytics', title: 'Result Analysis Report (RAR)', text: 'Track strengths and gaps over time with every test.' },
  { icon: 'ti-trophy', title: 'Confidence boost', text: 'Step-by-step readiness for the full mock tests.' },
  { icon: 'ti-list-check', title: 'Complete syllabus coverage', text: 'Maths, English, Creative Writing, VR and NVR, all covered.' }
];

const TT_STEPS: TtStep[] = [
  { title: 'Sign up', text: 'Quick online registration that takes less than 2 minutes.' },
  { title: 'Choose your tests', text: 'Weekly tests from October to February, aligned to your child\'s school term.' },
  { title: 'Start practising', text: 'Sit topic-specific tests online or at a centre near you.' }
];

const TT_TESTIMONIALS: TtTestimonial[] = [
  { initial: 'B', name: 'Alumni parent', place: 'Barnet', text: 'These weekly tests helped my child gradually master every topic. By the time the full mocks started, she was confident and ready.' },
  { initial: 'C', name: 'Alumni parent', place: 'Chelmsford', text: 'The Topic Tests are a game-changer. They highlight weak areas and give students a structured routine that actually works.' },
  { initial: 'M', name: 'Alumni parent', place: 'Milton Keynes', text: 'We saw consistent improvement week after week. The systematic approach made all the difference in our child\'s preparation.' }
];

const TT_FAQS: TtFaq[] = [
  { q: 'What age group are these tests for?', a: 'Students preparing for 11+ exams, typically Year 5 pupils aged 9–10. Tests align to major exam boards including GL Assessment, CEM and CSSE.' },
  { q: 'Are the tests online or face to face?', a: 'Both. Use the Format filter in the calendar to see only online or only face-to-face sessions.' },
  { q: 'How long does each test take?', a: 'About 30–45 minutes per weekly topic test, designed to fit into a busy school week.' },
  { q: 'Which exam formats do you cover?', a: 'CSSE, CEM, GL Assessment, PACE and FSCE, across Harrow, Barnet, Chelmsford, Brentwood, Amersham, Milton Keynes and Bexley.' },
  { q: 'Do I receive feedback after each test?', a: 'Yes. Every test includes a Result Analysis Report (RAR) with strengths, weaknesses and focus areas for the following week.' }
];

const TT_PAGE_SIZE = 10;
const TT_SCHEDULE_URL = 'https://drive.google.com/uc?export=download&id=1vai5uaSTJbMAtf0DXcfWizs8BPYAcZ9z';

// ── Perf config ──────────────────────────────────────────────
const TT_MAX_PAGES = 50;
const TT_BATCH = 4;                    // API pages requested in parallel
const TT_CACHE_TTL = 5 * 60 * 1000;    // 5 min in-memory cache
const TT_SEARCH_DEBOUNCE = 200;
const TT_ALLOWED_TYPES = ['GL', 'CEM', 'CSSE', 'FSCE', 'SET', 'GL Redbridge', 'GL Kent', 'GLQE Barnet', 'GL Bucks', 'GL Bexley', 'GL HBS'];
const TT_11PLUS_CATEGORY_IDS = [16, 19, 39, 28, 29, 30, 31, 32, 33];

// Module-level cache: survives route changes, cleared on full reload.
let ttFilterCache: { at: number; data: any[] } | null = null;
let ttListCache: { at: number; userId: number; results: any[] } | null = null;

@Component({
  selector: 'app-eleven-topic-test',
  templateUrl: './eleven-topic-test.component.html',
  styleUrls: ['./eleven-topic-test.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ElevenTopicTestComponent implements OnInit, OnDestroy {

  title = '11+ Topic Tests – Weekly Preparation | Sehyog';

  // ── Content ────────────────────────────────────────────────
  guide = TT_GUIDE;
  stats = TT_STATS;
  subjects = TT_SUBJECTS;
  features = TT_FEATURES;
  benefits = TT_BENEFITS;
  steps = TT_STEPS;
  testimonials = TT_TESTIMONIALS;
  faqs = TT_FAQS;
  starCount = [1, 2, 3, 4, 5];

  // ── Filters ────────────────────────────────────────────────
  filters: any = { search: '', pattern: null, mode: '', month: '', center: '' };
  patternTypes: any[] = [];
  centers: string[] = [];
  months: TtMonth[] = [];

  // ── Data ───────────────────────────────────────────────────
  testList: any[] = [];
  visibleList: any[] = [];
  hasMore = false;
  private allResults: any[] = [];
  visibleCount = TT_PAGE_SIZE;
  loading = true;

  ngUnsubscribe = new Subject<void>();
  private search$ = new Subject<void>();

  private MONTH_FULL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  private MON_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  private DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  private monthMap: any = { JAN: 0, FEB: 1, MAR: 2, APR: 3, MAY: 4, JUN: 5, JUL: 6, AUG: 7, SEP: 8, OCT: 9, NOV: 10, DEC: 11 };
  private fullMonthMap: any = { JANUARY: 0, FEBRUARY: 1, MARCH: 2, APRIL: 3, MAY: 4, JUNE: 5, JULY: 6, AUGUST: 7, SEPTEMBER: 8, OCTOBER: 9, NOVEMBER: 10, DECEMBER: 11 };

  constructor(
    private router: ActivatedRoute,
    private titleService: Title,
    private categoryProduct: CmsserviceService,
    private route: Router,
    private ref: ChangeDetectorRef
  ) {}

  // ── Lifecycle ──────────────────────────────────────────────
  ngOnInit(): void {
    this.titleService.setTitle(this.title);
    this.loadAssets();

    this.search$
      .pipe(debounceTime(TT_SEARCH_DEBOUNCE), takeUntil(this.ngUnsubscribe))
      .subscribe(() => this.applyFilters());

    // Filters and tests load in parallel (previously sequential).
    this.loadFilters();
    this.loadTests();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  // ── Fonts + icons (dynamic, duplicate-guarded) ─────────────
  private loadAssets(): void {
    this.addPreconnect('https://fonts.googleapis.com', false);
    this.addPreconnect('https://fonts.gstatic.com', true);
    this.addPreconnect('https://cdn.jsdelivr.net', true);

    this.addStylesheet('tt-fonts-link', 'family=Space+Grotesk',
      'https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=Nunito:wght@400;600;700;800&display=swap');
    this.addStylesheet('tt-tabler-link', 'tabler-icons',
      'https://cdn.jsdelivr.net/npm/@tabler/icons-webfont@2.47.0/tabler-icons.min.css',
      'https://unpkg.com/@tabler/icons-webfont@2.47.0/tabler-icons.min.css');
  }

  private addPreconnect(href: string, crossOrigin: boolean): void {
    if (document.querySelector('link[rel="preconnect"][href="' + href + '"]')) { return; }
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = href;
    if (crossOrigin) { link.crossOrigin = 'anonymous'; }
    document.head.appendChild(link);
  }

  private addStylesheet(id: string, hrefMatch: string, href: string, fallback?: string): void {
    if (document.getElementById(id)) { return; }
    if (document.querySelector('link[href*="' + hrefMatch + '"]')) { return; }
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = href;
    if (fallback) {
      link.onerror = function () { link.onerror = null; link.href = fallback; };
    }
    document.head.appendChild(link);
  }

  // ── Filter options ─────────────────────────────────────────
  private loadFilters(): void {
    if (ttFilterCache && Date.now() - ttFilterCache.at < TT_CACHE_TTL) {
      this.patternTypes = ttFilterCache.data;
      return;
    }
    this.categoryProduct.getTestFilters()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((res: any) => {
        const data = (res && res.data) ? res.data : {};
        const allTypes = data.type ? data.type : [];
        this.patternTypes = allTypes.filter((t: any) => TT_ALLOWED_TYPES.indexOf(t.type) !== -1);
        ttFilterCache = { at: Date.now(), data: this.patternTypes };
        this.ref.markForCheck();
      }, (err: any) => {
        console.error('getTestFilters error', err);
      });
  }

  // ── Topic tests: page 1 renders immediately, rest in parallel batches ──
  private loadTests(): void {
    const userId = +(localStorage.getItem('userid') || 0) || 0;

    if (ttListCache && ttListCache.userId === userId && Date.now() - ttListCache.at < TT_CACHE_TTL) {
      this.allResults = ttListCache.results;
      this.refreshDerived();
      this.loading = false;
      this.applyFilters();
      return;
    }

    this.allResults = [];
    this.loading = true;
    let hadError = false;

    const request = (page: number): Observable<any> =>
      this.categoryProduct.getTestList(this.buildQuery(userId, page)).pipe(
        take(1),
        catchError((err: any) => {
          console.error('Error fetching topic tests page', err);
          hadError = true;
          return of(null);
        })
      );

    // Ingests one page; returns true if more pages remain.
    const handle = (res: any): boolean => {
      if (!res) { return false; }
      const chunk: any[] = res.data ? res.data : [];
      if (chunk.length === 0) { return false; }
      const topics = chunk.filter((t: any) => this.isTopicTest(t));
      this.decorate(topics);
      this.allResults = this.allResults.concat(topics);
      const extra = (res.extra && typeof res.extra.extraCount === 'number') ? res.extra.extraCount : null;
      return extra === null ? true : extra > 0;
    };

    const showPartial = () => {
      if (!this.allResults.length) { return; }
      this.refreshDerived();
      this.loading = false;
      this.applyFilters(false);
    };

    const finalise = () => {
      if (!hadError) { ttListCache = { at: Date.now(), userId, results: this.allResults }; }
      this.refreshDerived();
      this.loading = false;
      this.applyFilters(false);
    };

    const fetchBatch = (start: number) => {
      if (start > TT_MAX_PAGES) { finalise(); return; }
      const end = Math.min(start + TT_BATCH - 1, TT_MAX_PAGES);
      const calls: Observable<any>[] = [];
      for (let p = start; p <= end; p++) { calls.push(request(p)); }

      forkJoin(calls)
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe((results: any[]) => {
          let more = true;
          for (const res of results) {
            if (!more) { break; }
            more = handle(res);
          }
          if (more) { showPartial(); fetchBatch(end + 1); } else { finalise(); }
        });
    };

    request(1)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((res: any) => {
        if (handle(res)) { showPartial(); fetchBatch(2); } else { finalise(); }
      });
  }

  private buildQuery(userId: number, page: number): any {
    return {
      release: 'new', exam_type: 'both', search: '',
      course: [], category: [], type: [],
      user_id: userId, page: page
    };
  }

  private refreshDerived(): void {
    this.centers = this.buildCenters(this.allResults);
    this.months = this.buildMonths(this.allResults);
  }

  private isTopicTest(t: any): boolean {
    if (!t) { return false; }
    const cid = Number(t.category_id);
    const name = (t.test_name || '').toString().toLowerCase();
    const cat = (t.category || '').toString().toLowerCase();
    const course = (t.course || '').toString().toLowerCase();
    const courseId = Number(t.course_id);
    const is11Plus = course === '11plus' || courseId === 1 ||
      TT_11PLUS_CATEGORY_IDS.indexOf(cid) !== -1 ||
      name.indexOf('11+') !== -1 || name.indexOf('11plus') !== -1;
    if (!is11Plus) { return false; }
    return cid === 19 || name.indexOf('topic') !== -1 || cat.indexOf('topic') !== -1;
  }

  // ── Pre-compute display + filter fields once ───────────────
  private decorate(list: any[]): void {
    for (const t of list) {
      const raw = (t && t.test_date) ? String(t.test_date).trim() : '';
      const ts = this.parseDateString(raw);
      t._range = /^from\s+/i.test(raw);
      t._rangeLabel = this.trimChar(raw.replace(/^from\s+/i, ''), 11);
      t._rawLabel = this.trimChar(raw, 11) || 'TBC';
      t._hasDate = ts != null;
      if (ts != null) {
        const d = new Date(ts);
        t._dow = this.DOW[d.getUTCDay()];
        t._day = d.getUTCDate();
        t._mon = this.MON_SHORT[d.getUTCMonth()];
        t._monthIdx = d.getUTCMonth();
        t._ts = ts;
      } else {
        t._monthIdx = -1;
        const tn = this.parseDateString((t && t.test_name) || '');
        t._ts = tn != null ? tn : Number.NaN;
      }
      t._online = t.exam_type === 'Online Session';
      t._loc = ((t.all_location || t.location || '') + '').trim();
      t._codes = this.paperCodes(t.test_name);
      t._cleanName = this.cleanName(t.test_name);

      // Template strings (no function calls in template)
      t._locShort = this.trimChar(t._loc);
      t._catShort = this.trimChar(t.category);
      t._typeShort = this.trimChar(t.type);

      // Lowercased filter fields
      t._typeLc = ((t.type || '') + '').toLowerCase();
      t._nameLc = ((t.test_name || '') + '').toLowerCase();
      t._searchLc = [t.test_name, t._loc, t.category, t.type].join(' ').toLowerCase();
    }
  }

  /** Bracketed paper codes in a test name: "(MN1)", "(ECOMP01)". */
  paperCodes(name: string): string[] {
    const out: string[] = [];
    const re = /\(([A-Z]{1,6}\d{1,3})\)/g;
    let m: any;
    while ((m = re.exec(name || '')) !== null) { out.push(m[1]); }
    return out;
  }

  /** Drops the repeated " - 11+ Topic Tests - 2026-27 Series" tail. */
  cleanName(name: string): string {
    return (name || '').replace(/\s*-\s*(11\+\s*)?Topic Tests?\s*-\s*\d{4}-\d{2}\s*Series\s*$/i, '');
  }

  // ── Filters ────────────────────────────────────────────────
  onSearch(): void {
    this.search$.next();
  }

  applyFilters(resetPaging: boolean = true): void {
    let list = this.allResults;

    if (this.filters.pattern != null) {
      const label = this.getPatternLabel().toLowerCase();
      if (label) {
        list = list.filter((t: any) => t._typeLc.indexOf(label) !== -1 || t._nameLc.indexOf(label) !== -1);
      }
    }
    if (this.filters.mode) {
      const mode = this.filters.mode;
      list = list.filter((t: any) => t.exam_type === mode);
    }
    if (this.filters.month !== '' && this.filters.month != null) {
      const mi = +this.filters.month;
      list = list.filter((t: any) => t._monthIdx === mi);
    }
    if (this.filters.center) {
      const c = this.filters.center;
      list = list.filter((t: any) => t._loc === c);
    }
    const q = ((this.filters.search || '') + '').toLowerCase().trim();
    if (q) {
      list = list.filter((t: any) => t._searchLc.indexOf(q) !== -1);
    }

    this.testList = this.sortUpcomingFirst(list);
    if (resetPaging) { this.visibleCount = TT_PAGE_SIZE; }
    this.updateVisible();
    this.ref.markForCheck();
  }

  resetFilters(): void {
    this.filters = { search: '', pattern: null, mode: '', month: '', center: '' };
    this.applyFilters();
  }

  loadMore(): void {
    this.visibleCount += TT_PAGE_SIZE;
    this.updateVisible();
  }

  private updateVisible(): void {
    this.visibleList = this.testList.slice(0, this.visibleCount);
    this.hasMore = this.testList.length > this.visibleCount;
  }

  getPatternLabel(): string {
    const meta = this.patternTypes.find((p: any) => String(p.type_id) === String(this.filters.pattern));
    return meta ? (meta.type || '') : '';
  }

  private buildCenters(list: any[]): string[] {
    const set: any = {};
    for (const t of list) { if (t._loc) { set[t._loc] = true; } }
    return Object.keys(set).sort();
  }

  /** Months that actually have sessions, in academic order (Sep → Aug). */
  private buildMonths(list: any[]): TtMonth[] {
    const set: any = {};
    for (const t of list) { if (t._monthIdx > -1) { set[t._monthIdx] = true; } }
    const order = [8, 9, 10, 11, 0, 1, 2, 3, 4, 5, 6, 7];
    return order.filter(i => set[i]).map(i => ({ value: i, label: this.MONTH_FULL[i] }));
  }

  // ── Date parsing ───────────────────────────────────────────
  private parseDateString(s: string): number | null {
    if (!s) { return null; }
    const str = s.trim();
    let m: any;

    m = str.match(/(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})/);
    if (m) {
      let y = parseInt(m[3], 10);
      if (y < 100) { y += 2000; }
      return Date.UTC(y, parseInt(m[2], 10) - 1, parseInt(m[1], 10));
    }
    m = str.match(/(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (m) { return Date.UTC(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10)); }

    m = str.match(/(?:from\s+)?(?:mon|tue|wed|thu|fri|sat|sun)?\s*([A-Za-z]+)\s+(\d{1,2})(?:st|nd|rd|th)?(?:,?\s*(\d{4}))?/i);
    if (m) {
      const monName = m[1].toUpperCase();
      let idx = this.fullMonthMap[monName];
      if (idx == null) { idx = this.monthMap[monName.substring(0, 3)]; }
      if (idx != null) {
        const yy = m[3] ? parseInt(m[3], 10) : new Date().getFullYear();
        return Date.UTC(yy, idx, parseInt(m[2], 10));
      }
    }
    m = str.match(/(\d{1,2})\s+([A-Za-z]{3,})\s+(\d{4})/);
    if (m) {
      const mn = m[2].toUpperCase();
      let mi = this.fullMonthMap[mn];
      if (mi == null) { mi = this.monthMap[mn.substring(0, 3)]; }
      if (mi != null) { return Date.UTC(parseInt(m[3], 10), mi, parseInt(m[1], 10)); }
    }
    return null;
  }

  private sortUpcomingFirst(list: any[]): any[] {
    const n = new Date();
    const today = Date.UTC(n.getFullYear(), n.getMonth(), n.getDate());
    const future: any[] = [], past: any[] = [], unknown: any[] = [];
    for (const t of list) {
      if (t._ts == null || isNaN(t._ts)) { unknown.push(t); }
      else if (t._ts >= today) { future.push(t); }
      else { past.push(t); }
    }
    future.sort((a, b) => a._ts - b._ts);
    past.sort((a, b) => b._ts - a._ts);
    return future.concat(past, unknown);
  }

  // ── Template helpers ───────────────────────────────────────
  trimChar(str: string, length: number = 30): string {
    if (!str) { return ''; }
    return str.length < length ? str : str.substring(0, length) + '...';
  }

  /** Track by object identity so rows are reused across filters/paging. */
  trackByIdx(i: number, t: any): any { return t || i; }

  scrollTo(id: string, event?: Event): void {
    if (event) { event.preventDefault(); }
    const el = document.getElementById(id);
    if (el) { el.scrollIntoView({ behavior: 'smooth' }); }
  }

  downloadCalendar(event?: Event): void {
    if (event) { event.preventDefault(); }
    window.location.href = TT_SCHEDULE_URL;
  }
}
